import { useEffect, useState } from "react";
import "@/App.css";
import "./styles/themes.css";  // Theme system CSS
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { ThemeProvider } from "./contexts/ThemeContext";
import LandingPage from "./pages/LandingPage";
import ConversionLanding from "./pages/ConversionLanding";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import CreateStory from "./pages/CreateStory";
import StoryReader from "./pages/StoryReader";
import DemoStory from "./pages/DemoStory";
import Settings from "./pages/Settings";
import Contact from "./pages/Contact";
import Pricing from "./pages/Pricing";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import AGB from "./pages/AGB";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import Widerruf from "./pages/Widerruf";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import FAQ from "./pages/FAQ";
import About from "./pages/About";
import VerifyEmail from "./pages/VerifyEmail";
import Unsubscribe from "./pages/Unsubscribe";
import BlogList from "./pages/BlogList";
import BlogPost from "./pages/BlogPost";
import { Toaster } from "./components/ui/sonner";
import CookieBanner from "./components/CookieBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Check for session_id in URL fragment
    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      try {
        const response = await axios.post(`${API}/auth/session`, {}, {
          headers: { 'X-Session-ID': sessionId }
        });
        setUser(response.data);
        localStorage.setItem('token', response.data.session_token);
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        console.error('Session processing failed:', error);
      }
      setLoading(false);
      return;
    }

    // Check for existing token
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout failed:', error);
    }
    localStorage.removeItem('token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
      </div>
    );
  }

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    // Also update localStorage for persistence
    if (updatedUser) {
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
        return response.data;
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
    return null;
  };

  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
          <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
          <Route path="/index.html" element={<Navigate to="/" replace />} />
          <Route path="/start" element={<ConversionLanding />} />
          <Route
            path="/auth"
            element={
              user
                ? <Navigate to="/dashboard" />
                : <AuthPage setUser={setUser} />
            }
          />
          <Route path="/dashboard" element={user ? <Dashboard user={user} logout={logout} updateUser={updateUser} refreshUser={refreshUser} /> : <Navigate to="/" />} />
          <Route path="/create" element={user ? <CreateStory user={user} /> : <Navigate to="/" />} />
          <Route path="/demo-story" element={<DemoStory />} />
          <Route path="/story/:id" element={user ? <StoryReader user={user} /> : <Navigate to="/" />} />
          <Route path="/settings" element={user ? <Settings user={user} logout={logout} updateUser={updateUser} refreshUser={refreshUser} /> : <Navigate to="/" />} />
          <Route path="/contact" element={<Contact user={user} logout={logout} />} />
          <Route path="/pricing" element={user ? <Pricing user={user} /> : <Navigate to="/" />} />
          <Route path="/checkout-success" element={user ? <CheckoutSuccess refreshUser={refreshUser} /> : <Navigate to="/" />} />
          <Route path="/agb" element={<AGB />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/privacy" element={<Datenschutz />} />
          <Route path="/widerruf" element={<Widerruf />} />
          <Route path="/withdrawal" element={<Widerruf />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/about" element={<About />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:postId" element={<BlogPost />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
        <CookieBanner />
        <Toaster />
      </div>
    </ThemeProvider>
  );
}

export default App;
